import type { TcdlVerb } from './types.js';
import type { Verification, VerificationKind } from './verifications.js';

/**
 * Der Classic-Befehlskatalog, im Code statt in der Datenbank.
 *
 * Classic fuehrt ihn in `tcdlactions` und `tcdlverifications`, je mit einer
 * `_lang`-Tabelle fuer die zweisprachigen Namen und der Spalte `engineid`. Der Grund
 * ist dort zwingend: Engines werden als JAR zur Laufzeit nachgeladen, der Server kann
 * nicht wissen, was eine Engine kann.
 *
 * In NG gilt das nicht. Die Engine ist ein versioniertes Paket, das mitgebaut wird —
 * ein Tippfehler faellt beim Uebersetzen auf statt im Testlauf, ein umbenannter Befehl
 * wird vom Refactoring mitgezogen, ein neuer Befehl braucht keine Migration, und die
 * Aenderung ist im Review sichtbar (NEX-1088).
 *
 * Uebernommen aus `create_tables_to_testengineer.xml` und den
 * `initial_data_engine_*.xml` der Classic-Quelle: 63 Aktionen, 17 Verifikationen,
 * davon 56 generisch.
 */

export type CatalogKind = 'action' | 'verification';

/** Die Engine-Familien aus Classics `engineid`. Fehlt sie, gilt der Befehl generisch. */
export type CatalogEngine = 'browser' | 'document' | 'graph' | 'mobile' | 'spreadsheet' | 'windows';

export interface CatalogEntry {
  /** Classic `sname` — der Schluessel, unter dem Classic den Eintrag fuehrt. */
  key: string;
  kind: CatalogKind;
  /** Deutscher Befehlsname, wie er in TCDL steht. */
  de: string;
  /** Englischer Befehlsname; TCDL ist zweisprachig (Classic: die `_lang`-Tabellen). */
  en: string;
  /** Classic `sebnf`, z. B. `"Command, Object Value"`. */
  grammar: string;
  /**
   * Alte Schreibweisen, die Classic als Benutzeraktion weiterfuehrt (`TCDLUSERACTIONS`) —
   * etwa `SPEICHERN TABELLENZELLE` nach der Umbenennung in `ZUWEISEN TABELLENZELLE`.
   */
  aliases?: readonly { de: string; en: string }[];
  /** Fehlt bei den generischen Befehlen — die gelten fuer jedes Zielsystem. */
  engine?: CatalogEngine;
  /** Das TCDL-Verb, auf das der Parser diesen Befehl abbildet. */
  verb?: TcdlVerb;
  /** Die Pruefung, auf die engine-core diese Verifikation abbildet. */
  verification?: VerificationKind;
  /**
   * Warum diese Engine den Eintrag nicht ausfuehrt.
   *
   * **Ein Grund schlaegt eine Umsetzung.** `verb` und `verification` sagen, worauf der
   * Parser abbildet; `excuse` beantwortet die andere Frage — fuehrt diese Engine das
   * aus? Ein Eintrag darf beides tragen: `IST MAXIMIERT` ist eine Verifikation, die
   * engine-core kennt, und die ein Browser trotzdem nicht beantwortet.
   *
   * Kein Eintrag darf keines von beidem tragen. `catalog.spec.ts` prueft das. Damit
   * ist Vollstaendigkeit nachweisbar statt behauptet, und ein spaeter ergaenzter
   * Classic-Befehl faellt auf.
   */
  excuse?: string;
}

export const CLASSIC_CATALOG: readonly CatalogEntry[] = [
  { key: "APPLICATION_START", kind: "action", de: "ANWENDUNG STARTEN", en: "APPLICATION START", grammar: "Command, Object Value", verb: "ANWENDUNG_STARTEN", excuse: "Ein Browser startet keine fremden Anwendungen. Fuer den Browser selbst gibt es BROWSER STARTEN." },
  { key: "APPLICATION_STOP", kind: "action", de: "ANWENDUNG STOPPEN", en: "APPLICATION STOP", grammar: "Command, Object", verb: "ANWENDUNG_STOPPEN", excuse: "Ein Browser stoppt keine fremden Anwendungen. Fuer den Browser selbst gibt es BROWSER STOPPEN." },
  { key: "ASSIGN", kind: "action", de: "ZUWEISEN", en: "ASSIGN", grammar: "Command, Value Value", verb: "ZUWEISEN" },
  { key: "ASSIGN_PROPERTY", kind: "action", de: "ZUWEISEN EIGENSCHAFT", en: "ASSIGN PROPERTY", grammar: "Command, Object Value", verb: "ZUWEISEN_EIGENSCHAFT" },
  { key: "ASSIGN_TABLE_CELL", kind: "action", de: "ZUWEISEN TABELLENZELLE", en: "ASSIGN TABLE CELL", grammar: "Command, Object Value", verb: "ZUWEISEN_TABELLENZELLE", aliases: [{ de: "SPEICHERN TABELLENZELLE", en: "SAVE TABLE CELL" }] },
  { key: "ASSIGN_TEXT", kind: "action", de: "ZUWEISEN TEXT", en: "ASSIGN TEXT", grammar: "Command, Object Value", verb: "ZUWEISEN_TEXT" },
  { key: "CHECK", kind: "action", de: "ANKREUZEN", en: "CHECK", grammar: "Command, Object", verb: "ANKREUZEN" },
  { key: "CLEAR_TEXT", kind: "action", de: "TEXT LÖSCHEN", en: "CLEAR TEXT", grammar: "Command, Object", verb: "TEXT_LOESCHEN", aliases: [{ de: "LÖSCHE TEXT", en: "DELETE TEXT" }] },
  { key: "CREATE_SCREENSHOT", kind: "action", de: "ERZEUGE SCREENSHOT", en: "CREATE SCREENSHOT", grammar: "Command, Object Value", verb: "ERZEUGE_SCREENSHOT" },
  { key: "DISPLAY", kind: "action", de: "ANZEIGEN", en: "DISPLAY", grammar: "Command, Object [Value]", verb: "ANZEIGEN" },
  { key: "ENTER_IN", kind: "action", de: "EINGABE IN", en: "ENTER IN", grammar: "Command, Object Value", verb: "EINGABE_IN" },
  { key: "ENTER_PASSWORD", kind: "action", de: "EINGABE PASSWORT", en: "ENTER PASSWORD", grammar: "Command, Object Value", verb: "EINGABE_PASSWORT" },
  { key: "EXECUTE", kind: "action", de: "AUSFÜHREN", en: "EXECUTE", grammar: "Command, Value [Value]", verb: "AUSFUEHREN" },
  { key: "EXECUTE_IF_OBJECT_EXISTS", kind: "action", de: "AUSFÜHREN WENN OBJEKT VORHANDEN", en: "EXECUTE IF OBJECT EXISTS", grammar: "Command, Object Value", verb: "AUSFUEHREN" },
  { key: "EXECUTE_IF_OBJECT_NOT_EXISTS", kind: "action", de: "AUSFÜHREN WENN OBJEKT NICHT VORHANDEN", en: "EXECUTE IF OBJECT NOT EXISTS", grammar: "Command, Object Value", verb: "AUSFUEHREN" },
  { key: "EXECUTE_IF_WINDOW_EXISTS", kind: "action", de: "AUSFÜHREN WENN FENSTER VORHANDEN", en: "EXECUTE IF WINDOW EXISTS", grammar: "Command, Object Value", verb: "AUSFUEHREN" },
  { key: "EXECUTE_IF_WINDOW_NOT_EXISTS", kind: "action", de: "AUSFÜHREN WENN FENSTER NICHT VORHANDEN", en: "EXECUTE IF WINDOW NOT EXISTS", grammar: "Command, Object Value", verb: "AUSFUEHREN" },
  { key: "LOG", kind: "action", de: "PROTOKOLLIERE", en: "LOG", grammar: "Command, Value", verb: "PROTOKOLLIERE" },
  { key: "LOG_DOCUMENT", kind: "action", de: "PROTOKOLLIERE DOKUMENT", en: "LOG DOCUMENT", grammar: "Command, Value", excuse: "Im Bestand nicht genutzt — nicht umgesetzt." },
  { key: "LOG_TABLE_ROW", kind: "action", de: "PROTOKOLLIERE TABELLENZEILE", en: "LOG TABLE ROW", grammar: "Command, Object Value", verb: "PROTOKOLLIERE_TABELLENZEILE" },
  { key: "LOG_WITH_SCREENSHOT", kind: "action", de: "PROTOKOLLIERE MIT SCREENSHOT", en: "LOG WITH SCREENSHOT", grammar: "Command, Object Value", verb: "PROTOKOLLIERE_MIT_SCREENSHOT" },
  { key: "MANUAL_TESTSTEP", kind: "action", de: "MANUELLER TESTSCHRITT", en: "MANUAL TESTSTEP", grammar: "Command, Value Value", verb: "MANUELLER_TESTSCHRITT" },
  { key: "MEASUREMENT_PAUSE", kind: "action", de: "MESSUNG PAUSE", en: "MEASUREMENT PAUSE", grammar: "Command, Value", excuse: "Performance-Messung; als Ausnahme gefuehrt (engine-backlog.md, Story 1 Out of Scope)." },
  { key: "MEASUREMENT_RESUME", kind: "action", de: "MESSUNG FORTSETZEN", en: "MEASUREMENT RESUME", grammar: "Command, Value", excuse: "Performance-Messung; als Ausnahme gefuehrt (engine-backlog.md, Story 1 Out of Scope)." },
  { key: "MEASUREMENT_START", kind: "action", de: "MESSUNG START", en: "MEASUREMENT START", grammar: "Command, Value", excuse: "Performance-Messung; als Ausnahme gefuehrt (engine-backlog.md, Story 1 Out of Scope)." },
  { key: "MEASUREMENT_STOP", kind: "action", de: "MESSUNG STOPP", en: "MEASUREMENT STOP", grammar: "Command, Value", excuse: "Performance-Messung; als Ausnahme gefuehrt (engine-backlog.md, Story 1 Out of Scope)." },
  { key: "MEASUREMENT_WRITE", kind: "action", de: "MESSUNG SCHREIBEN", en: "MEASUREMENT WRITE", grammar: "Command, Value", excuse: "Performance-Messung; als Ausnahme gefuehrt (engine-backlog.md, Story 1 Out of Scope)." },
  { key: "PRESS", kind: "action", de: "DRÜCKE", en: "PRESS", grammar: "Command, Object", verb: "DRUECKE" },
  { key: "SELECT_LINK", kind: "action", de: "AUSWAHL LINK", en: "SELECT LINK", grammar: "Command, Object Value", verb: "AUSWAHL_LINK" },
  { key: "SELECT_MENU", kind: "action", de: "AUSWAHL MENÜ", en: "SELECT MENU", grammar: "Command, Object", verb: "AUSWAHL_MENUE" },
  { key: "SELECT_RECORD", kind: "action", de: "AUSWAHL EINTRAG", en: "SELECT RECORD", grammar: "Command, Object Value", verb: "AUSWAHL_EINTRAG" },
  { key: "SEND_DOCUMENT", kind: "action", de: "SCHICKE DOKUMENT", en: "SEND DOCUMENT", grammar: "Command, Value", excuse: "Dokumentversand ist Sache des Backends, nicht der Engine." },
  { key: "SET_FOCUS", kind: "action", de: "SETZE FOKUS", en: "SET FOCUS", grammar: "Command, Object", verb: "SETZE_FOKUS" },
  { key: "TYPE_KEYS", kind: "action", de: "DRÜCKE TASTEN", en: "TYPE KEYS", grammar: "Command, Value", verb: "DRUECKE_TASTEN" },
  { key: "UNCHECK", kind: "action", de: "KREUZ ENTFERNEN", en: "UNCHECK", grammar: "Command, Object", verb: "KREUZ_ENTFERNEN" },
  { key: "WAIT", kind: "action", de: "WARTE", en: "WAIT", grammar: "Command, Value", verb: "WARTE" },
  { key: "WAIT_FOR_TABLE_CONTENT", kind: "action", de: "WARTE AUF TABELLENINHALT", en: "WAIT FOR TABLE CONTENT", grammar: "Command, Object Value", verb: "WARTE_AUF_TABELLENINHALT" },
  { key: "WINDOW_MAXIMISE", kind: "action", de: "FENSTER MAXIMIEREN", en: "WINDOW MAXIMISE", grammar: "Command, Object", verb: "FENSTER_MAXIMIEREN" },
  { key: "WINDOW_MINIMISE", kind: "action", de: "FENSTER MINIMIEREN", en: "WINDOW MINIMISE", grammar: "Command, Object", verb: "FENSTER_MINIMIEREN", excuse: "Ein Browser kann sich nicht minimieren — BrowserDriver bringt minimizeWindow deshalb nicht mit." },
  { key: "WINDOW_RESTORE", kind: "action", de: "FENSTER WIEDERHERSTELLEN", en: "WINDOW RESTORE", grammar: "Command, Object", verb: "FENSTER_WIEDERHERSTELLEN" },
  { key: "WINDOW_SET_POSITION", kind: "action", de: "FENSTERPOSITION SETZEN", en: "WINDOW SET POSITION", grammar: "Command, Object Value", verb: "FENSTERPOSITION_SETZEN", excuse: "Playwright kann ein Browserfenster nicht positionieren — es gibt dafuer keine API." },
  { key: "WINDOW_SET_SIZE", kind: "action", de: "FENSTERGRÖSSE SETZEN", en: "WINDOW SET SIZE", grammar: "Command, Object Value", verb: "FENSTERGROESSE_SETZEN" },
  { key: "BROWSER_DISPLAY", kind: "action", de: "BROWSER ANZEIGEN", en: "BROWSER DISPLAY", grammar: "Command, Value", engine: "browser", verb: "BROWSER_ANZEIGEN" },
  { key: "BROWSER_LOGIN", kind: "action", de: "BROWSER ANMELDEN", en: "BROWSER LOGIN", grammar: "Command, Value Value", engine: "browser", verb: "BROWSER_ANMELDEN" },
  { key: "BROWSER_START", kind: "action", de: "BROWSER STARTEN", en: "BROWSER START", grammar: "Command, Value [Value]", engine: "browser", verb: "BROWSER_STARTEN" },
  { key: "BROWSER_STOP", kind: "action", de: "BROWSER STOPPEN", en: "BROWSER STOP", grammar: "Command, [Value]", engine: "browser", verb: "BROWSER_STOPPEN" },
  { key: "BROWSER_TAB_CLOSE", kind: "action", de: "BROWSER TAB SCHLIESSEN", en: "BROWSER TAB CLOSE", grammar: "Command, Object", engine: "browser", verb: "BROWSER_TAB_SCHLIESSEN" },
  { key: "BROWSER_TAB_OPEN", kind: "action", de: "BROWSER TAB ÖFFNEN", en: "BROWSER TAB OPEN", grammar: "Command, [Value]", engine: "browser", verb: "BROWSER_TAB_OEFFNEN" },
  { key: "WEBPAGE_LOAD", kind: "action", de: "WEBSEITE LADEN", en: "WEBPAGE LOAD", grammar: "Command, Value", engine: "browser", verb: "WEBSEITE_LADEN" },
  { key: "EMAIL ABFRAGEN", kind: "action", de: "EMAIL ABFRAGEN", en: "EMAIL QUERY", grammar: "Command, Value", engine: "graph", excuse: "Gehoert zur graph-Engine; die Browser-Engine fuehrt sie nicht aus." },
  { key: "SETZE MAILBOX ADRESSE", kind: "action", de: "SETZE MAILBOX ADRESSE", en: "SET MAILBOX ADDRESS", grammar: "Command, Value", engine: "graph", excuse: "Gehoert zur graph-Engine; die Browser-Engine fuehrt sie nicht aus." },
  { key: "SETZE MAILBOX CLIENTID", kind: "action", de: "SETZE MAILBOX CLIENTID", en: "SET MAILBOX CLIENTID", grammar: "Command, Value", engine: "graph", excuse: "Gehoert zur graph-Engine; die Browser-Engine fuehrt sie nicht aus." },
  { key: "SETZE MAILBOX CLIENTSECRET", kind: "action", de: "SETZE MAILBOX CLIENTSECRET", en: "SET MAILBOX CLIENTSECRET", grammar: "Command, Value", engine: "graph", excuse: "Gehoert zur graph-Engine; die Browser-Engine fuehrt sie nicht aus." },
  { key: "SETZE MAILBOX TENANTID", kind: "action", de: "SETZE MAILBOX TENANTID", en: "SET MAILBOX TENANTID", grammar: "Command, Value", engine: "graph", excuse: "Gehoert zur graph-Engine; die Browser-Engine fuehrt sie nicht aus." },
  { key: "ZUWEISEN EMAIL ABSATZ", kind: "action", de: "ZUWEISEN EMAIL ABSATZ", en: "ASSIGN EMAIL PARAGRAPH", grammar: "Command, Value", engine: "graph", excuse: "Gehoert zur graph-Engine; die Browser-Engine fuehrt sie nicht aus." },
  { key: "ZUWEISEN EMAIL ABSATZ ANZAHL", kind: "action", de: "ZUWEISEN EMAIL ABSATZ ANZAHL", en: "ASSIGN EMAIL COUNT PARAGRAPHS", grammar: "Command, Value", engine: "graph", excuse: "Gehoert zur graph-Engine; die Browser-Engine fuehrt sie nicht aus." },
  { key: "ZUWEISEN EMAIL BETREFF", kind: "action", de: "ZUWEISEN EMAIL BETREFF", en: "ASSIGN EMAIL SUBJECT", grammar: "Command, Value", engine: "graph", excuse: "Gehoert zur graph-Engine; die Browser-Engine fuehrt sie nicht aus." },
  { key: "MOBILE_START", kind: "action", de: "MOBILE STARTEN", en: "MOBILE START", grammar: "Command, Value [Value]", engine: "mobile", excuse: "Gehoert zur mobile-Engine; die Browser-Engine fuehrt sie nicht aus." },
  { key: "MOBILE_STOP", kind: "action", de: "MOBILE STOPPEN", en: "MOBILE STOP", grammar: "Command, [Value]", engine: "mobile", excuse: "Gehoert zur mobile-Engine; die Browser-Engine fuehrt sie nicht aus." },
  { key: "PRESS_BACK", kind: "action", de: "DRÜCKE ZURÜCK", en: "PRESS BACK", grammar: "Command, ", engine: "mobile", excuse: "Gehoert zur mobile-Engine; die Browser-Engine fuehrt sie nicht aus." },
  { key: "SCROLL", kind: "action", de: "SCROLLEN", en: "SCROLL", grammar: "Command, Object Value", engine: "mobile", excuse: "Gehoert zur mobile-Engine; die Browser-Engine fuehrt sie nicht aus." },
  { key: "TO_CLIPBOARD", kind: "action", de: "IN ZWISCHENABLAGE", en: "TO CLIPBOARD", grammar: "Command, Object Value", engine: "spreadsheet", excuse: "Gehoert zur spreadsheet-Engine; die Browser-Engine fuehrt sie nicht aus." },
  { key: "PRESS_CONTEXT_MENU", kind: "action", de: "DRÜCKE KONTEXTMENÜ", en: "PRESS CONTEXT MENU", grammar: "Command, Object Value", engine: "windows", excuse: "Gehoert zur windows-Engine; die Browser-Engine fuehrt sie nicht aus." },
  { key: "ACTIVE", kind: "verification", de: "AKTIV", en: "ACTIVE", grammar: "Command, Object", verification: "AKTIV" },
  { key: "CHECKED", kind: "verification", de: "ANGEKREUZT", en: "CHECKED", grammar: "Command, Object", verification: "ANGEKREUZT" },
  { key: "CONTAINING", kind: "verification", de: "ENTHALTEN", en: "CONTAINING", grammar: "Command, Object Value", verification: "ENTHALTEN" },
  { key: "CONTENT", kind: "verification", de: "INHALT", en: "CONTENT", grammar: "Command, Object Value", verification: "INHALT" },
  { key: "EMPTY", kind: "verification", de: "LEER", en: "EMPTY", grammar: "Command, Object", verification: "LEER" },
  { key: "EXISTING", kind: "verification", de: "VORHANDEN", en: "EXISTING", grammar: "Command, Object", verification: "VORHANDEN" },
  { key: "FOCUSED", kind: "verification", de: "FOKUSIERT", en: "FOCUSED", grammar: "Command, Object", verification: "FOKUSIERT" },
  { key: "MAXIMISED", kind: "verification", de: "MAXIMIERT", en: "MAXIMISED", grammar: "Command, Object", verification: "MAXIMIERT", excuse: "Fensterzustand und Bildvergleich beantwortet ein Browser nicht — siehe BrowserDriver.unsupportedVerifications." },
  { key: "MINIMISED", kind: "verification", de: "MINIMIERT", en: "MINIMISED", grammar: "Command, Object", verification: "MINIMIERT", excuse: "Fensterzustand und Bildvergleich beantwortet ein Browser nicht — siehe BrowserDriver.unsupportedVerifications." },
  { key: "PROPERTY", kind: "verification", de: "EIGENSCHAFT", en: "PROPERTY", grammar: "Command, Object Value", verification: "EIGENSCHAFT" },
  { key: "SCREENSHOT", kind: "verification", de: "SCREENSHOT", en: "SCREENSHOT", grammar: "Command, Object Value", verification: "SCREENSHOT", excuse: "Fensterzustand und Bildvergleich beantwortet ein Browser nicht — siehe BrowserDriver.unsupportedVerifications." },
  { key: "SCREENSHOT_COMPARE", kind: "verification", de: "SCREENSHOTVERGLEICH", en: "SCREENSHOT COMPARE", grammar: "Command, Value", verification: "SCREENSHOTVERGLEICH", excuse: "Fensterzustand und Bildvergleich beantwortet ein Browser nicht — siehe BrowserDriver.unsupportedVerifications." },
  { key: "SELECTED", kind: "verification", de: "AUSGEWÄHLT", en: "SELECTED", grammar: "Command, Object Value", verification: "AUSGEWÄHLT" },
  { key: "VISIBLE", kind: "verification", de: "SICHTBAR", en: "VISIBLE", grammar: "Command, Object", verification: "SICHTBAR" },
  { key: "CHECK_DOCUMENT", kind: "verification", de: "DOKUMENT VORLAGE", en: "DOCUMENT TEMPLATE", grammar: "Command, Value Value ", engine: "document", excuse: "Gehoert zur document-Engine; die Browser-Engine fuehrt sie nicht aus." },
  { key: "BETREFF", kind: "verification", de: "EMAIL BETREFF", en: "EMAIL SUBJECT", grammar: "Command, Value", engine: "graph", excuse: "Gehoert zur graph-Engine; die Browser-Engine fuehrt sie nicht aus." },
  { key: "EMAIL ABSATZ", kind: "verification", de: "EMAIL ABSATZ", en: "EMAIL PARAGRAPH", grammar: "Command, Value Value", engine: "graph", excuse: "Gehoert zur graph-Engine; die Browser-Engine fuehrt sie nicht aus." },
];

/**
 * Alle Eintraege, die diese Engine ausfuehrt — Grundlage fuer die Vervollstaendigung
 * im Editor.
 *
 * Ein Grund schlaegt eine Umsetzung: `IST MAXIMIERT` bildet auf eine Pruefung ab, die
 * engine-core kennt, wird hier aber nicht angeboten, weil ein Browser sie nicht
 * beantwortet. Anzubieten, was der Lauf danach verweigert, waere die schlechtere
 * Auskunft.
 */
export function supportedCommands(): readonly CatalogEntry[] {
  return CLASSIC_CATALOG.filter(
    (e) => (e.verb !== undefined || e.verification !== undefined) && e.excuse === undefined,
  );
}

/**
 * Sucht einen Eintrag ueber seinen Namen, deutsch oder englisch, auch ueber einen Alias.
 *
 * Der Vergleich ist unabhaengig von Gross- und Kleinschreibung und von der Anzahl der
 * Leerzeichen: In Bestandsdaten steht `AUSFÜHREN  WENN OBJEKT VORHANDEN` genauso wie
 * mit einfachem Abstand.
 */
export function findCommand(name: string): CatalogEntry | undefined {
  const gesucht = normalisiereName(name);
  return CLASSIC_CATALOG.find((e) =>
    [e, ...(e.aliases ?? [])].some(
      (name) => normalisiereName(name.de) === gesucht || normalisiereName(name.en) === gesucht,
    ),
  );
}

/** Gross/Klein und Leerzeichen vereinheitlicht — so vergleichen Katalog und Parser Namen. */
export function normalisiereName(name: string): string {
  return name.trim().replace(/\s+/gu, ' ').toUpperCase();
}

// ---------------------------------------------------------------------------------------
// Erkennung: welcher Katalogbefehl am Anfang einer TCDL-Zeile steht.
//
// Sie liegt hier und nicht im Parser, weil das Frontend den Katalog direkt aus der Quelle
// bindet und Befehle im Editor mit genau den Regeln faerben soll, mit denen sie laufen
// (NEX-1279). Deshalb darf diese Datei zur Laufzeit nichts importieren:
// architecture.spec.ts prueft das.
// ---------------------------------------------------------------------------------------

/**
 * Schreibvarianten von Pruefungen aus Bestaenden. Der Katalog fuehrt AUSGEWÄHLT mit Umlaut;
 * Exporte tragen mal ihn, mal die Umschreibung — dieselbe Lage wie bei GROESSER/GRÖSSER.
 */
export const PRUEF_SCHREIBVARIANTEN: Readonly<Record<string, VerificationKind>> = {
  AUSGEWAEHLT: 'AUSGEWÄHLT',
  FOKUSSIERT: 'FOKUSIERT',
};

/**
 * Wie ein Katalogname in einer Zeile stehen darf: Gross/Klein egal, beliebig viele
 * Leerzeichen, Umlaute auch umschrieben (`DRUECKE`, `DRUCKE`) — so stehen sie im Bestand.
 */
const UMSCHRIFT: Record<string, string> = { Ä: '(?:Ä|AE?)', Ö: '(?:Ö|OE?)', Ü: '(?:Ü|UE?)' };

function schreibweise(name: string): string {
  return name
    .split(' ')
    .map((wort) => [...wort].map((z) => UMSCHRIFT[z] ?? z.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&')).join(''))
    .join('\\s+');
}

/** Ein gewoehnlicher Name endet am Wortende. */
const WORTENDE = '(?![\\p{L}\\p{N}_])';

/**
 * Ein Name, der einen anderen um ganze Woerter verlaengert, endet erst an Komma,
 * Anfuehrungszeichen, Zeilenende oder Bedingung. Ein blosses Wortende reicht nicht —
 * Classic-Objektnamen wie `Text Name` oder `Zurück-Button` beginnen oft mit dem
 * Zusatzwort, und `ZUWEISEN Text Name` ist ein ZUWEISEN, kein ZUWEISEN TEXT (NEX-1447).
 */
const NAMENSENDE = '(?=\\s*(?:[,"]|$)|\\s+(?:WENN|IF)\\b)';

/**
 * Steht der Wert der Kurzform nur in Anfuehrungszeichen, kann das Zusatzwort kein Name
 * sein — dann folgt dem laengeren Namen das Ziel auch ohne Komma:
 * `WARTE AUF TABELLENINHALT tbl, "5"`. Bindestrich und Punkt setzen einen Namen fort.
 */
const ZUSATZENDE = '(?=\\s|[,"]|$)';

/**
 * Kurzformen, deren Wert ausschliesslich in Anfuehrungszeichen steht. Die Grammatik
 * reicht dafuer nicht: PROTOKOLLIERE hat wie WARTE nur einen Wert, liest aber auch
 * Freitext wie `PROTOKOLLIERE Tabellenzeile geprüft`.
 */
const NUR_WERT_IN_ANFUEHRUNGSZEICHEN = new Set(['WAIT']);

/** Eine Erkennungsregel: das Muster am Zeilenanfang und das Verb, auf das es abbildet. */
export interface VerbRegel {
  muster: RegExp;
  verb: TcdlVerb;
  /** Nur bei `verb === 'NICHT_UMGESETZT'`. */
  catalogKey?: string;
  eintrag: CatalogEntry;
  /** Nur bei einem Alias: der aktuelle Name in derselben Sprache (NEX-1789). */
  aktuell?: string;
}

function namenVon(e: CatalogEntry): string[] {
  return [e, ...(e.aliases ?? [])].flatMap((n) => [n.de, n.en]).map(normalisiereName);
}

/**
 * Erzeugt die Verb-Erkennung aus dem Katalog: jeder Name, deutsch, englisch und Alias.
 * Ein neuer Katalogeintrag mit `verb` wird damit erkannt, ohne dass der Parser
 * angefasst wird (NEX-1215).
 *
 * Die Regeln sind nach Namenslaenge absteigend sortiert: Das laengere Verb gewinnt,
 * sonst wird `TASTEN` zum Ziel eines gewoehnlichen DRUECKE.
 *
 * Auch Eintraege ohne `verb` bekommen eine Regel, sonst griffe die Kurzform:
 * `ASSIGN EMAIL SUBJECT, "v"` liefe als ZUWEISEN und meldete gruen. Sie werden
 * NICHT_UMGESETZT mit ihrem Schluessel und enden rot mit dem Grund aus dem Katalog.
 */
export function verbRegeln(katalog: readonly CatalogEntry[]): VerbRegel[] {
  const eintraege = katalog.filter((e) => e.kind === 'action');
  const kurzformen = (e: CatalogEntry) =>
    eintraege.filter((k) =>
      namenVon(k).some((kurz) => namenVon(e).some((name) => name.startsWith(`${kurz} `))),
    );

  return eintraege
    .flatMap((e) => {
      // Je Eintrag, nicht je Name: SPEICHERN TABELLENZELLE verlaengert selbst nichts,
      // ist aber dieselbe Unterform wie ZUWEISEN TABELLENZELLE und endet genauso.
      const kurz = kurzformen(e);
      const ende =
        kurz.length === 0
          ? WORTENDE
          : kurz.every((k) => NUR_WERT_IN_ANFUEHRUNGSZEICHEN.has(k.key))
            ? ZUSATZENDE
            : NAMENSENDE;
      const ziel: Pick<VerbRegel, 'verb' | 'catalogKey'> = e.verb
        ? { verb: e.verb }
        : { verb: 'NICHT_UMGESETZT', catalogKey: e.key };
      // Jeder Alias gilt als veraltet und nennt den aktuellen Namen seiner Sprache — ein
      // spaeter ergaenzter Alias wird so ohne weitere Aenderung im Protokoll vermerkt.
      const namen: { name: string; aktuell?: string }[] = [
        { name: e.de },
        { name: e.en },
        ...(e.aliases ?? []).flatMap((a) => [
          { name: a.de, aktuell: e.de },
          { name: a.en, aktuell: e.en },
        ]),
      ];
      return namen.map(({ name, aktuell }) => {
        const normalisiert = normalisiereName(name);
        const regel: VerbRegel = {
          muster: new RegExp(`^${schreibweise(normalisiert)}${ende}`, 'iu'),
          ...ziel,
          eintrag: e,
        };
        if (aktuell) regel.aktuell = aktuell;
        return { laenge: normalisiert.length, regel };
      });
    })
    .sort((a, b) => b.laenge - a.laenge)
    .map(({ regel }) => regel);
}

const VERB_REGELN = verbRegeln(CLASSIC_CATALOG);

/**
 * `IST INHALT, feld, "Meier"`, `IST NICHT SICHTBAR bOk`, `IS NOT VISIBLE, bOk`.
 *
 * Beide Schreibweisen kommen vor — mit und ohne Komma hinter der Pruefung. Die Negation
 * steht in Classic zwischen Verb und Pruefung und ist dort ein eigener
 * Grammatikbestandteil, kein Zusatz am Ende. Die Namen kommen aus dem Katalog, dazu die
 * Schreibvarianten (FOKUSSIERT). Eine Pruefung ohne Umsetzung (`IST EMAIL BETREFF`)
 * endet als NICHT_UMGESETZT mit ihrem Katalog-Schluessel.
 */
const PRUEF_REGELN: ReadonlyArray<{ muster: RegExp; eintrag: CatalogEntry }> = CLASSIC_CATALOG.filter(
  (e) => e.kind === 'verification',
)
  .flatMap((eintrag) =>
    [
      eintrag.de,
      eintrag.en,
      ...Object.keys(PRUEF_SCHREIBVARIANTEN).filter((v) => PRUEF_SCHREIBVARIANTEN[v] === eintrag.verification),
    ].map((name) => ({ name: normalisiereName(name), eintrag })),
  )
  .sort((a, b) => b.name.length - a.name.length)
  .map(({ name, eintrag }) => ({
    muster: new RegExp(`^IST?\\s+((?:NICHT|NOT)\\s+)?${schreibweise(name)}${WORTENDE}`, 'iu'),
    eintrag,
  }));

/** Was `erkenneVerb` erkannt hat, samt dem Text, der dafuer stand. */
export interface Erkennung {
  verb: TcdlVerb;
  /** Der erkannte Anfang der Zeile — der Parser schneidet genau ihn ab. */
  treffer: string;
  catalogKey?: string;
  verification?: Verification;
  /** Der Katalogeintrag hinter dem Verb, soweit es einen gibt. */
  eintrag?: CatalogEntry;
  /**
   * Nur bei einer alten Schreibweise (Alias): der Name, wie er in der Zeile steht, und der
   * aktuelle Name. Der Parser macht daraus den Hinweis im Protokoll (NEX-1789).
   */
  veraltet?: { alt: string; neu: string };
  /**
   * Wie lang der Befehl in der Zeile ist — fuer die Faerbung im Editor. Meist die Laenge
   * von `treffer`; bei AUSFUEHREN bleibt `treffer` leer, der Befehl ist trotzdem sichtbar.
   */
  laenge: number;
}

/** Diese Kurzformen weichen einer Zeile mit dem Wort ENGINE, so wie bisher. */
const WEICHT_ENGINE: ReadonlySet<TcdlVerb> = new Set(['ZUWEISEN', 'DRUECKE']);

function eintragZuVerb(verb: TcdlVerb): CatalogEntry | undefined {
  return CLASSIC_CATALOG.find((e) => e.verb === verb);
}

/**
 * Bestimmt das Verb einer Zeile. Die Namen kommen aus dem Katalog (`VERB_REGELN`,
 * `PRUEF_REGELN`); von Hand bleibt nur, was kein Katalogname ist.
 */
export function erkenneVerb(text: string): Erkennung {
  // Vor allen anderen: Eine IST-Zeile ist nur dann eine Verifikation, wenn die Pruefung
  // bekannt ist. `IST GRUENLICH` wird UNBEKANNT und faellt auf, statt auf das
  // naechstbeste Verb geraten zu werden.
  if (/^IST?\b/iu.test(text)) {
    for (const { muster, eintrag } of PRUEF_REGELN) {
      const treffer = muster.exec(text);
      if (!treffer) continue;
      const gemeinsam = { treffer: treffer[0], laenge: treffer[0].length, eintrag };
      if (!eintrag.verification) return { verb: 'NICHT_UMGESETZT', catalogKey: eintrag.key, ...gemeinsam };
      return { verb: 'IST', verification: { kind: eintrag.verification, negated: Boolean(treffer[1]) }, ...gemeinsam };
    }
    return { verb: 'UNBEKANNT', treffer: '', laenge: 0 };
  }

  // Auch mit kaputtem Umlaut aus alten Exporten — das liest kein Katalogname. Der Treffer
  // bleibt leer wie bisher; fuer die Anzeige zaehlt der volle Name, wenn es einer ist.
  const ausfuehren = /^AUSF\p{L}+/iu.exec(text);
  if (ausfuehren) {
    const regel = VERB_REGELN.find((r) => r.muster.test(text));
    const name = regel?.muster.exec(text)?.[0] ?? ausfuehren[0];
    return { verb: 'AUSFUEHREN', treffer: '', laenge: name.length, eintrag: regel?.eintrag ?? eintragZuVerb('AUSFUEHREN') };
  }

  for (const regel of VERB_REGELN) {
    const treffer = regel.muster.exec(text);
    if (!treffer) continue;
    if (WEICHT_ENGINE.has(regel.verb) && /\bENGINE\b/iu.test(text)) break;
    const erkannt: Erkennung = {
      verb: regel.verb,
      treffer: treffer[0],
      laenge: treffer[0].length,
      catalogKey: regel.catalogKey,
      eintrag: regel.eintrag,
    };
    if (regel.aktuell) erkannt.veraltet = { alt: treffer[0].trimEnd(), neu: regel.aktuell };
    return erkannt;
  }

  if (/\bENGINE\b/iu.test(text)) return { verb: 'ENGINE_WECHSEL', treffer: '', laenge: 0 };

  // Engine-prefixed forms such as `2BITS ZUWEISEN ...` or `2BITS DRÜCKE ...`
  for (const [verb, wort] of [
    ['ZUWEISEN', 'ZUWEISEN'],
    ['DRUECKE', 'DR[ÜU]CKE'],
  ] as const) {
    if (!new RegExp(`\\b${wort}\\b`, 'iu').test(text)) continue;
    const treffer = new RegExp(`^(?:\\S+\\s+)?${wort}`, 'iu').exec(text)?.[0] ?? '';
    return { verb, treffer, laenge: treffer.length, eintrag: eintragZuVerb(verb) };
  }

  return { verb: 'UNBEKANNT', treffer: '', laenge: 0 };
}
